import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToPricing = (e) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing');
    pricingSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text">
            AI-to-Poster
          </Link>
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={scrollToPricing} className="text-gray-600 hover:text-gray-900 transition-colors">
              Pricing
            </button>
            <Link 
              to="/generator" 
              className="bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              Create
            </Link>
          </nav>
          <button className="md:hidden text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>
        </div>
      </div>
    </motion.header>
  );
};

const ImageCarousel = () => {
  const images = [
    "https://i.etsystatic.com/52145706/r/il/b2a6f1/6094193211/il_1588xN.6094193211_7yuz.jpg",
    "https://i.etsystatic.com/52145706/r/il/ce96a0/6046107786/il_1588xN.6046107786_kblg.jpg",
    "https://i.etsystatic.com/52145706/r/il/2cf31d/6047529426/il_1588xN.6047529426_t7cb.jpg",
    "https://i.etsystatic.com/52145706/r/il/2f115d/6095989819/il_1588xN.6095989819_qdk5.jpg",
    "https://i.etsystatic.com/52145706/r/il/f970d1/6226391203/il_1588xN.6226391203_9vfw.jpg",
    "https://i.etsystatic.com/52145706/r/il/8ac7b2/6263163513/il_1588xN.6263163513_9w51.jpg",
    "https://i.etsystatic.com/52145706/r/il/f5eb9e/6197349510/il_1588xN.6197349510_cw6e.jpg",
    "https://i.etsystatic.com/52145706/r/il/f4013d/6196513660/il_1588xN.6196513660_gyp6.jpg",
    "https://i.etsystatic.com/52145706/r/il/2ef2cc/6271388817/il_1588xN.6271388817_1orl.jpg"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const visibleImages = [
    images[(currentIndex - 1 + images.length) % images.length],
    images[currentIndex],
    images[(currentIndex + 1) % images.length],
  ];

  return (
    <div className="relative w-full overflow-hidden py-8">
      <div className="container mx-auto px-4">
        <div className="relative flex justify-center items-center gap-4">
          {visibleImages.map((image, index) => (
            <motion.div
              key={index}
              className={`relative cursor-pointer ${
                index === 1 ? 'w-96 z-20' : 'w-72 opacity-50 z-10 hover:opacity-70'
              } aspect-[3/4] rounded-xl overflow-hidden shadow-xl transition-all duration-300`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: index === 1 ? 1 : 0.5, scale: index === 1 ? 1 : 0.8 }}
              transition={{ duration: 0.5 }}
              onClick={() => {
                if (index === 0) {
                  goToSlide((currentIndex - 1 + images.length) % images.length);
                } else if (index === 2) {
                  goToSlide((currentIndex + 1) % images.length);
                }
              }}
            >
              <img
                src={image}
                alt={`Poster ${index}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>

        <div className="flex justify-center mt-4 gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-gradient-to-r from-[#FF3366] to-[#7C3AED] w-6'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCreateClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
    } else {
      navigate('/generator');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF]">
      <Header />
      
      {/* Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-5%] right-[-5%] w-1/3 h-1/3 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-[-5%] left-[-5%] w-1/3 h-1/3 bg-gradient-to-tr from-blue-200 to-teal-200 rounded-full blur-3xl opacity-30"></div>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-28 pb-8 relative">
        <motion.div 
          className="text-center max-w-4xl mx-auto"
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Transform Your Ideas Into{' '}
            <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B98] text-transparent bg-clip-text">
              Beautiful
            </span>{' '}
            <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-transparent bg-clip-text">
              Posters
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Create stunning AI-generated posters and get them shipped directly to your door. 
            No design experience needed.
          </p>
          <Link 
            onClick={handleCreateClick}
            className="group relative inline-flex items-center justify-center px-8 py-4 font-medium text-white transition-all duration-300 ease-in-out"
          >
            <span className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#FF3366] to-[#7C3AED] rounded-full"></span>
            <span className="relative">Create Your Poster</span>
          </Link>
        </motion.div>
      </section>

      {/* Image Carousel */}
      <ImageCarousel />

      {/* How It Works - Reduced padding */}
      <section className="py-20 relative overflow-hidden backdrop-blur-sm bg-white/30">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-3xl font-bold text-center mb-16 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            How It Works
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "1. Describe Your Vision",
                description: "Enter any prompt or idea you have in mind for your perfect poster.",
                icon: "✨",
                gradient: "from-[#FF3366] to-[#FF6B98]"
              },
              {
                title: "2. Generate & Customize",
                description: "Our AI will create multiple designs for you to choose from and customize.",
                icon: "🎨",
                gradient: "from-[#4F46E5] to-[#7C3AED]"
              },
              {
                title: "3. Receive Your Poster",
                description: "Select your size and finish. We'll print and ship your poster directly to you.",
                icon: "📦",
                gradient: "from-[#FF6B98] to-[#7C3AED]"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="absolute inset-0 bg-gradient-to-r w-full h-full blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"
                     style={{background: `linear-gradient(to right, var(--tw-gradient-stops))`}}></div>
                <div className="relative p-8 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                  <div className={`text-4xl mb-6 bg-gradient-to-r ${step.gradient} text-transparent bg-clip-text`}>{step.icon}</div>
                  <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 scroll-mt-20">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-3xl font-bold text-center mb-16 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Simple Pricing
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Beginner",
                price: "£15",
                period: "/ month",
                features: [
                  "100 AI Generations",
                  "High-Quality Images",
                  "Multiple Download Formats (JPEG, PNG)",
                  "Basic Customization (colors, simple text)",
                  "Standard Processing Speed"
                ]
              },
              {
                name: "Hobbyist",
                price: "£30",
                period: "/ month",
                features: [
                  "250 AI Generations",
                  "Premium Quality Images (higher resolution)",
                  "Access to 10 Additional Poster Sizes",
                  "Portrait & Horizontal Options",
                  "Standard Processing Speed",
                  "Email Support (24-hour response)"
                ]
              },
              {
                name: "Artist",
                price: "£50",
                period: "/ month",
                features: [
                  "500 AI Generations",
                  "Maximum Quality Images (ultra-high resolution)",
                  "Fastest Processing Speed",
                  "Full Access to 10 Additional Poster Sizes",
                  "Free Shipping on Poster Orders",
                  "Access to Exclusive Templates",
                  "Storage for Saved Projects",
                  "Priority Support"
                ]
              }
            ].map((plan, index) => (
              <motion.div
                key={index}
                className="relative flex flex-col h-full p-6 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div>
                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-500 ml-1">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start text-gray-600">
                        <svg className="w-5 h-5 mr-2 text-green-500 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto">
                  <Link
                    onClick={handleCreateClick}
                    className="block w-full py-3 px-6 text-center text-white bg-gradient-to-r from-[#FF3366] to-[#7C3AED] rounded-full hover:shadow-lg transition-all duration-300"
                  >
                    Create
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Updated button text */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] opacity-90"></div>
        <div className="absolute inset-0 backdrop-blur-sm"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.h2 
            className="text-4xl font-bold mb-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Create Your Perfect Poster?
          </motion.h2>
          <Link 
            onClick={handleCreateClick}
            className="group relative inline-flex items-center justify-center px-8 py-4 font-medium transition-all duration-300 ease-in-out bg-white text-gray-900 rounded-full hover:shadow-2xl hover:-translate-y-1"
          >
            <span className="relative">Create Now</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-600 backdrop-blur-sm bg-white/30">
        <div className="container mx-auto px-4">
          <p>© 2024 AItoPoster. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
